<template>
  <div>
    <v-row class="ma-0 my-4 align-center justify-space-between">
      <h1 class="text-h5 font-weight-bold">Importar Billing desde hoja</h1>
    </v-row>

    <v-alert v-if="importError" type="error" dense outlined dismissible class="mb-3">{{ importError }}</v-alert>
    <v-alert v-if="importSuccess" type="success" dense outlined dismissible class="mb-3">
      Se importaron {{ importedCount }} registros correctamente.
    </v-alert>

    <v-card outlined class="pa-6" max-width="800">

      <!-- Paso 1: Elegir contrato -->
      <div class="text-subtitle-1 font-weight-medium mb-3">
        <v-icon small class="mr-1" color="primary">mdi-numeric-1-circle</v-icon>
        Elige el contrato
      </div>
      <v-row class="mb-4">
        <v-col cols="12" md="8">
          <v-autocomplete
            v-model="selectedContratoId"
            :items="contratos"
            item-text="codigoEmpresa"
            item-value="id"
            label="Contrato *"
            dense outlined
            :loading="loadingContratos"
            clearable
            @change="onContratoChange"
          />
        </v-col>
        <v-col v-if="empresaNombre" cols="12" md="4">
          <v-text-field
            :value="empresaNombre"
            label="Empresa"
            dense outlined
            readonly
            background-color="grey lighten-4"
            prepend-inner-icon="mdi-domain"
          />
        </v-col>
      </v-row>

      <v-divider class="mb-4" />

      <!-- Paso 2: Pegar texto o subir archivo -->
      <div class="text-subtitle-1 font-weight-medium mb-1">
        <v-icon small class="mr-1" color="primary">mdi-numeric-2-circle</v-icon>
        Pega el contenido o sube un archivo
      </div>
      <div class="text-body-2 grey--text text--darken-1 mb-3">
        Formato por fila:
        <code>mrrLocal,mes,año</code>
        &nbsp;—&nbsp;Ejemplo: <code>1500000,3,2025</code>
        <span v-if="selectedMoneda" class="ml-2">
          · Moneda: <strong>{{ selectedMoneda }}</strong> (heredada del contrato)
        </span>
      </div>

      <!-- Tabs: pegar texto o subir archivo -->
      <v-tabs v-model="tab" class="mb-4">
        <v-tab>Pegar texto</v-tab>
        <v-tab>Subir archivo</v-tab>
      </v-tabs>

      <v-tabs-items v-model="tab">
        <!-- Pegar texto -->
        <v-tab-item>
          <v-textarea
            v-model="csvText"
            outlined
            rows="8"
            placeholder="mrrLocal,mes,año
1500000,3,2025
2000000,3,2025"
            class="billing-csv-textarea"
            @input="parseCSV"
          />
        </v-tab-item>

        <!-- Subir archivo -->
        <v-tab-item>
          <v-file-input
            v-model="csvFile"
            label="Selecciona un archivo CSV o TXT"
            accept=".csv,.txt"
            outlined dense
            prepend-icon="mdi-file-upload-outline"
            @change="onFileChange"
          />
        </v-tab-item>
      </v-tabs-items>

      <!-- Preview -->
      <div v-if="preview.length > 0" class="mt-4">
        <div class="text-subtitle-1 font-weight-medium mb-2">
          Vista previa ({{ preview.length }} registros)
        </div>
        <v-simple-table dense class="elevation-1 mb-4">
          <template v-slot:default>
            <thead>
              <tr>
                <th class="text-right">MRR Local</th>
                <th>Mes</th>
                <th>Año</th>
                <th>Estado</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, i) in preview" :key="i" :class="row.valid ? '' : 'red lighten-5'">
                <td class="text-right">{{ row.priceBilling }}</td>
                <td>{{ row.monthBilling }}</td>
                <td>{{ row.yearBilling }}</td>
                <td>
                  <v-icon small :color="row.valid ? 'success' : 'error'">
                    {{ row.valid ? 'mdi-check-circle' : 'mdi-alert-circle' }}
                  </v-icon>
                  <span v-if="!row.valid" class="red--text caption ml-1">{{ row.errorMsg }}</span>
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>

        <div class="d-flex align-center justify-space-between">
          <div>
            <span class="success--text mr-3">
              <v-icon small color="success">mdi-check-circle</v-icon>
              {{ validRows }} válidos
            </span>
            <span v-if="invalidRows > 0" class="error--text">
              <v-icon small color="error">mdi-alert-circle</v-icon>
              {{ invalidRows }} con errores (se omitirán)
            </span>
          </div>
          <v-btn
            color="primary"
            :loading="importing"
            :disabled="validRows === 0 || !selectedContratoId"
            @click="importRecords"
          >
            <v-icon left>mdi-table-arrow-up</v-icon>Importar {{ validRows }} registros
          </v-btn>
        </div>

        <div v-if="!selectedContratoId" class="orange--text text-caption mt-2">
          <v-icon x-small color="orange">mdi-alert</v-icon>
          Debes seleccionar un contrato antes de importar.
        </div>
      </div>

      <div v-else-if="csvText || csvFile" class="grey--text text-center py-4">
        No se detectaron filas válidas.
      </div>
    </v-card>
  </div>
</template>

<script>
import BillingService from '../services/billing.service';
import ContratoService from '../services/contrato.service';
import CompanyService from '../services/companie.service';

export default {
  name: 'BillingImportar',
  data() {
    return {
      tab: 0,
      csvText: '',
      csvFile: null,
      preview: [],
      importing: false,
      loadingContratos: false,
      importError: null,
      importSuccess: false,
      importedCount: 0,
      selectedContratoId: null,
      selectedCompanyId: null,
      selectedMoneda: '',
      empresaNombre: '',
      contratos: [],
      companies: [],
    };
  },
  computed: {
    validRows() {
      return this.preview.filter(r => r.valid).length;
    },
    invalidRows() {
      return this.preview.filter(r => !r.valid).length;
    },
  },
  mounted() {
    this.loadSelects();
  },
  methods: {
    async loadSelects() {
      this.loadingContratos = true;
      const [compRes, contRes] = await Promise.all([
        CompanyService.getCompanies(1, 500, ''),
        ContratoService.getAll(),
      ]);
      if (compRes.status === 'ok') this.companies = compRes.data?.companies || compRes.data || [];
      if (contRes.status === 'ok') {
        const companies = this.companies;
        this.contratos = (contRes.data || []).map(c => {
          const empresa = companies.find(e => e.id === c.companyId);
          return {
            ...c,
            codigoEmpresa: empresa ? `${c.codigo} — ${empresa.name} — ${c.producto?.nombre || c.lineaNegocio || ''}` : c.codigo,
          };
        });
      }
      this.loadingContratos = false;
    },

    onContratoChange(contratoId) {
      const contrato = this.contratos.find(c => c.id === contratoId);
      if (contrato) {
        this.selectedCompanyId = contrato.companyId;
        const empresa = this.companies.find(e => e.id === contrato.companyId);
        this.empresaNombre = empresa ? empresa.name : '';
        this.selectedMoneda = empresa?.currency || 'CLP';
      } else {
        this.selectedCompanyId = null;
        this.empresaNombre = '';
        this.selectedMoneda = '';
      }
    },

    parseCSV() {
      this.importError = null;
      this.importSuccess = false;
      const lines = this.csvText
        .split('\n')
        .map(l => l.trim())
        .filter(l => l.length > 0);

      this.preview = lines.map(line => this.parseLine(line));
    },

    parseLine(line) {
      const parts = line.split(',').map(p => p.trim());
      const row = {
        raw: line,
        priceBilling: parts[0] || '',
        monthBilling: parts[1] || '',
        yearBilling: parts[2] || '',
        valid: false,
        errorMsg: '',
      };

      const price = Number(row.priceBilling);
      if (isNaN(price) || price <= 0) { row.errorMsg = 'mrrLocal inválido'; return row; }
      const month = Number(row.monthBilling);
      if (!Number.isInteger(month) || month < 1 || month > 12) {
        row.errorMsg = 'mes inválido (1-12)'; return row;
      }
      const year = Number(row.yearBilling);
      if (!Number.isInteger(year) || year < 2020 || year > 2100) {
        row.errorMsg = 'año inválido'; return row;
      }

      row.valid = true;
      return row;
    },

    async onFileChange(file) {
      if (!file) return;
      const text = await file.text();
      this.csvText = text;
      this.parseCSV();
    },

    async importRecords() {
      if (!this.selectedContratoId || !this.selectedCompanyId) {
        this.importError = 'Debes seleccionar un contrato primero.';
        return;
      }
      this.importing = true;
      this.importError = null;
      this.importSuccess = false;

      // Construir líneas en formato: companyId,priceBilling,currency,monthBilling,yearBilling,contratoId
      // La moneda viene del contrato seleccionado (no se pide al usuario)
      const currency = this.selectedMoneda || 'CLP';
      const validLines = this.preview
        .filter(r => r.valid)
        .map(r => `${this.selectedCompanyId},${r.priceBilling},${currency},${r.monthBilling},${r.yearBilling},${this.selectedContratoId}`);

      const res = await BillingService.createBulk(validLines);
      if (res.status === 'ok') {
        this.importedCount = validLines.length;
        this.importSuccess = true;
        this.csvText = '';
        this.csvFile = null;
        this.preview = [];
      } else {
        this.importError = 'Ocurrió un error al importar. Verifica los datos e intenta de nuevo.';
      }
      this.importing = false;
    },
  },
};
</script>

<style scoped>
.billing-csv-textarea :deep(textarea) {
  font-family: monospace;
  font-size: 13px;
}
</style>
